import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Star } from "lucide-react";
import { api, formatApiError, money, pct } from "@/lib/api";
import { PageHead } from "@/components/admin/PageHead";
import ListingFormDialog from "@/components/admin/ListingFormDialog";
import ConfirmDelete from "@/components/admin/ConfirmDelete";

const FIELDS = [
  { key: "ticker", label: "Ticker" },
  { key: "name", label: "Company name" },
  { key: "sector", label: "Sector", required: false },
  { key: "price", label: "Price (USD)", type: "number" },
  { key: "change_pct", label: "Day change (%)", type: "number" },
];
const EMPTY = { ticker: "", name: "", sector: "", price: "", change_pct: 0, description: "", featured: false };

export default function AdminStocks() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = useCallback(() => api.get("/stocks").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (s) => {
    try { await api.delete(`/admin/stocks/${s.id}`); toast.success(`${s.ticker} deleted`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-stocks-page">
      <PageHead title="Stocks" subtitle="Add listings and update prices manually.">
        <button onClick={() => setEditing(null)} className="btn-primary" data-testid="admin-add-stock-button"><Plus className="h-4 w-4" /> Add stock</button>
      </PageHead>
      <div className="panel overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead><tr className="border-b border-line text-slate-500">
            <th className="px-6 py-4 font-medium">Ticker</th><th className="px-6 py-4 font-medium">Company</th><th className="px-6 py-4 font-medium">Sector</th>
            <th className="px-6 py-4 text-right font-medium">Price</th><th className="px-6 py-4 text-right font-medium">Change</th><th className="px-6 py-4" />
          </tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-line/60 last:border-0" data-testid={`admin-stock-row-${s.ticker}`}>
                <td className="px-6 py-4 font-mono font-semibold text-white"><span className="inline-flex items-center gap-2">{s.ticker}{s.featured && <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />}</span></td>
                <td className="px-6 py-4 text-slate-300">{s.name}</td>
                <td className="px-6 py-4 text-slate-400">{s.sector}</td>
                <td className="px-6 py-4 text-right font-mono">{money(s.price)}</td>
                <td className={`px-6 py-4 text-right font-mono ${s.change_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>{pct(s.change_pct)}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={() => setEditing(s)} className="rounded-lg p-2 text-slate-500 transition-colors hover:bg-white/5 hover:text-white" data-testid={`admin-edit-stock-${s.ticker}`}><Pencil className="h-4 w-4" /></button>
                  <ConfirmDelete label={s.ticker} onConfirm={() => remove(s)} testId={`admin-delete-stock-${s.ticker}`} />
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">No stocks yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <ListingFormDialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)} item={editing}
        empty={EMPTY} fields={FIELDS} endpoint="stocks" assetType="stock" title="Stock" onSaved={load} />
    </div>
  );
}
